import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PORTRAITS } from "@/data/exercises";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Wand2, Lock } from "lucide-react";
import { Link } from "react-router-dom";

let robotAccess = null;
const getRobotAccess = () => (robotAccess ||= api.get("/avatars/robot/options").then((r) => r.data).catch(() => { robotAccess = null; return { allowed: false }; }));

const ROBOT_LABELS = {
  gender: ["Personne", { femme: "Femme", homme: "Homme" }],
  age: ["Âge", { enfant: "Enfant", ado: "Ado", jeune: "25 ans", adulte: "40 ans", a50: "50 ans", a60: "60 ans", a70: "70 ans", senior: "80 ans", a90: "90 ans et plus" }],
  skin: ["Teint", { claire: "Clair", mate: "Mat", foncee: "Foncé", asiatique: "Traits asiatiques" }],
  hair_color: ["Couleur des cheveux", { blancs: "Blancs", gris: "Gris", bruns: "Bruns", chatains: "Châtains", blonds: "Blonds", roux: "Roux", noirs: "Noirs" }],
  hair_style: ["Coiffure", { courts: "Courts", milongs: "Mi-longs", longs: "Longs", chignon: "Chignon", boucles: "Bouclés", chauve: "Chauve" }],
  glasses: ["Lunettes", { non: "Sans", rondes: "Rondes", fines: "Fines dorées" }],
  facial_hair: ["Barbe", { non: "Sans", moustache: "Moustache", barbe: "Barbe" }],
  accessory: ["Accessoire", { aucun: "Aucun", foulard: "Foulard", beret: "Béret", casquette: "Casquette", collier: "Collier", chapeau: "Chapeau de paille", gilet: "Gilet tricoté" }],
};
const ROBOT_DEFAULT = { gender: "femme", age: "senior", skin: "claire", hair_color: "blancs", hair_style: "courts", glasses: "non", facial_hair: "non", accessory: "aucun" };

function RobotBuilder({ onPick }) {
  const [t, setT] = useState(ROBOT_DEFAULT);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [drawnFor, setDrawnFor] = useState("");
  const same = drawnFor === JSON.stringify(t);
  const draw = async () => {
    setBusy(true);
    try { const { data } = await api.post("/avatars/robot", t); setResult(data.url); setDrawnFor(JSON.stringify(t)); }
    catch (e) { toast.error(e.response?.data?.detail || "Le portrait n'a pas pu être dessiné."); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-[1fr_220px] gap-6" data-testid="robot-builder">
      <div className="space-y-3">
        {Object.entries(ROBOT_LABELS).filter(([k]) => !(k === "facial_hair" && t.gender === "femme")).map(([k, [label, opts]]) => (
          <div key={k}>
            <p className="text-sm font-semibold text-gray-700 mb-1">{label}</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(opts).map(([v, l]) => (
                <button key={v} type="button" onClick={() => setT((o) => ({ ...o, [k]: v }))} data-testid={`robot-${k}-${v}`}
                  className={`h-9 px-3 rounded-full text-sm border transition-colors ${t[k] === v ? "bg-amber-100 border-amber-400 text-amber-900 font-semibold" : "bg-white border-gray-200 text-gray-700 hover:border-amber-300"}`}>{l}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col items-center gap-3">
        <div className="w-48 h-48 rounded-full overflow-hidden bg-[#FAF7F2] border-4 border-amber-100 flex items-center justify-center">
          {busy ? <Loader2 className="w-8 h-8 animate-spin text-amber-600" /> : result ? <img src={result} alt="Portrait-robot" className="w-full h-full object-cover object-top" data-testid="robot-result" /> : <Wand2 className="w-10 h-10 text-amber-300" />}
        </div>
        {!(result && same && !busy) && (
          <Button type="button" variant="outline" onClick={draw} disabled={busy} data-testid="robot-draw">
            {busy ? "Dessin en cours…" : result ? "Dessiner avec ces changements" : "Dessiner le portrait"}
          </Button>
        )}
        {result && !busy && <Button type="button" onClick={() => onPick(result)} data-testid="robot-use">Choisir ce portrait</Button>}
        <p className="text-xs text-gray-500 text-center">Dessiné dans le style des Gardiens. Quelques secondes la première fois.</p>
      </div>
    </div>
  );
}

function RobotLocked({ family }) {
  return (
    <div className="text-center py-6 px-4" data-testid="robot-locked">
      <Lock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
      <p className="font-serif-display text-xl mb-2">Le portrait-robot est réservé aux abonnés</p>
      <p className="text-gray-600 mb-5">
        {family ? "Il sera disponible dès que l'établissement ou le soignant de votre proche aura un abonnement Passerelle."
          : "Choisissez une formule Particulier ou Établissement pour dessiner des portraits sur mesure dans le style des Gardiens. Les 10 portraits de la liste restent disponibles gratuitement."}
      </p>
      {!family && <Link to="/abonnement" className="inline-flex h-11 items-center px-6 rounded-full bg-emerald-700 text-white font-semibold" data-testid="robot-locked-subscribe">Voir les formules</Link>}
    </div>
  );
}

function RobotTile({ onChange, compact }) {
  const [open, setOpen] = useState(false);
  const [access, setAccess] = useState(null);
  useEffect(() => { getRobotAccess().then(setAccess); }, []);
  const locked = access && !access.allowed;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="portrait-robot-open" title="Portrait-robot"
        className={`rounded-3xl border-4 border-dashed border-amber-200 bg-amber-50/50 text-center flex flex-col items-center justify-center transition-transform duration-300 hover:-translate-y-1 ${compact ? "p-1" : "p-2.5 sm:p-3"}`}>
        <span className="relative w-full aspect-square rounded-full flex items-center justify-center bg-white">
          <Wand2 className="w-1/3 h-1/3 text-amber-500" />
          {locked && <Lock className="absolute bottom-[12%] right-[12%] w-1/5 h-1/5 text-gray-500" data-testid="portrait-robot-lock" />}
        </span>
        {!compact && <p className="font-serif-display text-base sm:text-lg mt-2 leading-tight">Portrait-robot</p>}
        {!compact && <p className="text-xs text-amber-800 mt-0.5" data-testid="portrait-robot-subscribers">Réservé aux abonnés</p>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl font-normal">Portrait-robot</DialogTitle></DialogHeader>
          {locked ? <RobotLocked family={access.family} /> : <RobotBuilder onPick={(url) => { onChange(url); setOpen(false); }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PortraitGrid({ value, onChange, compact = false }) {
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-5" : "grid-cols-2 sm:grid-cols-5"}`} data-testid="portrait-grid">
      {value && !PORTRAITS.some((p) => p.image === value) && (
        <div className={`rounded-3xl border-4 border-amber-300 bg-amber-50 ${compact ? "p-1" : "p-2.5 sm:p-3"}`} data-testid="portrait-robot-current">
          <img src={value} alt="Portrait-robot" className="w-full aspect-square rounded-full object-cover object-top" />
          {!compact && <p className="font-serif-display text-base sm:text-lg mt-2 leading-tight text-center">Sur mesure</p>}
        </div>
      )}
      {PORTRAITS.map((p) => (
        <button key={p.id} type="button" onClick={() => onChange(p.image)} data-testid={`portrait-${p.id}`} data-selected={value === p.image ? "true" : "false"} title={p.label}
          className={`rounded-3xl border-4 text-center transition-[transform,border-color] duration-300 hover:-translate-y-1 ${compact ? "p-1" : "p-2.5 sm:p-3"} ${value === p.image ? "border-amber-300 bg-amber-50" : "border-transparent bg-[#FAF7F2]"}`}>
          <img src={p.image} alt={p.label} className="w-full aspect-square rounded-full object-cover object-top" />
          {!compact && <p className="font-serif-display text-base sm:text-lg mt-2 leading-tight">{p.label}</p>}
        </button>
      ))}
      <RobotTile onChange={onChange} compact={compact} />
    </div>
  );
}

export function PortraitEditor({ personName, current, onSave }) {
  const [sel, setSel] = useState(current || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await onSave(sel); toast.success(`Le portrait de ${personName} est enregistré`); }
    catch { toast.error("Enregistrement impossible."); }
    setBusy(false);
  };
  return (
    <div data-testid="portrait-editor">
      <PortraitGrid value={sel} onChange={setSel} />
      <Button className="mt-6" onClick={save} disabled={busy || !sel || sel === current} data-testid="portrait-save-button">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Enregistrer le portrait
      </Button>
    </div>
  );
}

export function PortraitDialogButton({ personName, current, onSave, children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="detail-portrait-button" title="Changer le portrait" className="rounded-full transition-transform duration-300 hover:scale-105">
        {children}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl font-normal">Le portrait de {personName}</DialogTitle></DialogHeader>
          <PortraitEditor personName={personName} current={current} onSave={async (a) => { await onSave(a); setOpen(false); }} />
        </DialogContent>
      </Dialog>
    </>
  );
}
